/**
 * Running cylinder balance for the office's Cylinder history.
 *
 * The ledger (orders.fn_tank_rental_history) only carries a balance on the
 * monthly BTRF rental rows — every delivery, pickup and audit adjustment in
 * between read "—", so nobody could see what a customer held on the day of a
 * given document. This walks the ledger in date order, per cylinder type, and
 * gives every row the balance AS OF that day:
 *
 *   - a rental row RESTATES the balance (its printed "New Balance");
 *   - a delivery adds, a pickup subtracts, an adjustment adds its signed qty.
 *
 * Same-day ordering matters: the biller counts activity strictly AFTER a
 * rental invoice's date (fn_tank_rental_compute: txn_date > btrf date), so a
 * movement dated the same day as the rental is already inside that restated
 * number. Movements therefore run first and the rental row last within a date.
 *
 * Before the first rental row there is no starting count on file, so the walk
 * starts at 0 and says so (`anchored: false`) — that is exactly the state that
 * produces a negative balance (more picked up than we ever invoiced out).
 */
export type LedgerKind = 'rental' | 'delivery' | 'pickup' | 'adjustment' | 'system_adjustment';

export interface LedgerRowIn {
  kind: LedgerKind;
  label: string;
  txn_date: string;
  quantity: number;
}

export interface LedgerBalance {
  /** Signed movement this row made (null on a rental row, which restates). */
  signed: number | null;
  /** Balance of this cylinder type at the END of this row's day — every row of
   *  one delivery shows the same number, so a swap (2 out, 3 back) never reads as
   *  a half-applied balance. */
  balance: number;
  /** false until a rental invoice has given this cylinder type a starting count. */
  anchored: boolean;
}

export function signedQty(r: { kind: LedgerKind; quantity: number }): number | null {
  switch (r.kind) {
    case 'rental': return null;
    case 'pickup': return -Math.abs(r.quantity);
    case 'delivery': return Math.abs(r.quantity);
    default: return r.quantity; // adjustments carry their own sign
  }
}

/** Returns one LedgerBalance per input row, in the INPUT order. */
export function runningBalances<T extends LedgerRowIn>(rows: T[]): LedgerBalance[] {
  const order = rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) =>
      a.r.txn_date < b.r.txn_date ? -1
        : a.r.txn_date > b.r.txn_date ? 1
          : (a.r.kind === 'rental' ? 1 : 0) - (b.r.kind === 'rental' ? 1 : 0) || a.i - b.i);
  const state = new Map<string, { balance: number; anchored: boolean }>();
  const out: LedgerBalance[] = new Array(rows.length);
  for (const { r, i } of order) {
    const s = state.get(r.label) ?? { balance: 0, anchored: false };
    const signed = signedQty(r);
    if (signed == null) { s.balance = r.quantity; s.anchored = true; }
    else s.balance += signed;
    state.set(r.label, s);
    out[i] = { signed, balance: s.balance, anchored: s.anchored };
  }
  // Settle every row of a (day, cylinder type) on that day's closing balance.
  const close = new Map<string, number>();
  for (const { r, i } of order) close.set(`${r.txn_date}|${r.label}`, out[i]!.balance);
  rows.forEach((r, i) => { out[i]!.balance = close.get(`${r.txn_date}|${r.label}`)!; });
  return out;
}
